import type { AuthOrigin } from '@repo/auth-utils'
import {
  getBrowserLocation,
  getConfiguredGraphQLUrl,
  getRegistrableDomain,
  parseProxyBypassHostSuffixes,
  resolveGraphQLUrl,
} from '@repo/env-graphql-url'
import type { BrowserLocation } from '@repo/env-graphql-url'
import { sleep } from 'utils/dom'

/**
 * HttpOnly `fm_session` bridge for hosts that proxy GraphQL at same-origin `/query`
 * and `/auth/*` to services-iam (e.g. `www.idea.ai`, `www.cf.land`, `freemode.ai`).
 *
 * POST `/auth/session` with the login JWT; IAM delegates to WordPress `/cfsecure/jwtauth`
 * and sets the cookie. The browser sends it on same-origin `/query`; the gateway mints
 * short-lived upstream JWTs so the session survives bearer expiry.
 *
 * Wired via `registerAuthLifecycleHooks` at each app entry point so fetch code only
 * ships in bundles that need it.
 */

/** Relative path so the request always resolves to the current origin. */
const SESSION_ENDPOINT = '/auth/session'

function isGraphQLQueryPath(pathname: string) {
  const path = pathname.replace(/\/+$/, '') || '/'
  return path === '/query' || path.endsWith('/query')
}

function isPreviewGraphqlBypassHost(location: BrowserLocation) {
  return parseProxyBypassHostSuffixes().some(suffix =>
    location.hostname.toLowerCase().endsWith(suffix),
  )
}

function configuredGraphqlSameRegistrableAsPage(location: BrowserLocation) {
  const configured = getConfiguredGraphQLUrl()
  if (!configured) {
    return false
  }

  const apiHostname = new URL(configured, location.origin).hostname
  return getRegistrableDomain(location.hostname) === getRegistrableDomain(apiHostname)
}

/** Production hosts that proxy `/auth/*` and same-origin `/query` (not `*.creativefabrica.com` Studio). */
function isKnownFirstPartySessionHost(hostname: string) {
  const host = hostname.toLowerCase()
  return (
    host.endsWith('.freemode.ai') ||
    host === 'freemode.ai' ||
    host.endsWith('.cf.land') ||
    host.endsWith('.idea.ai')
  )
}

/**
 * True when this origin should POST `/auth/session` (same-origin IAM proxy).
 * False on `studio.creativefabrica.com`, Preview bypass hosts, and unrelated GraphQL hosts.
 */
export function usesFirstPartySessionBridge(
  location: BrowserLocation | null = getBrowserLocation(),
): boolean {
  if (!location) {
    return false
  }

  try {
    const resolved = new URL(resolveGraphQLUrl({ location }))
    if (resolved.hostname === location.hostname && isGraphQLQueryPath(resolved.pathname)) {
      return true
    }

    if (
      isKnownFirstPartySessionHost(location.hostname) &&
      !isPreviewGraphqlBypassHost(location) &&
      configuredGraphqlSameRegistrableAsPage(location)
    ) {
      return true
    }

    return false
  } catch {
    return false
  }
}

export interface EstablishSessionOptions {
  /**
   * Skip the resolver check and always attempt the session POST.
   * Prefer the automatic gate; kept for tests and exceptional call sites.
   */
  force?: boolean
}

export interface UpgradeSessionOptions extends EstablishSessionOptions {
  clearToken?: () => void
}

/**
 * Upgrades the freshly-saved login token into the durable `fm_session` cookie.
 *
 * Best-effort: on failure the caller retains the localStorage bearer written
 * by `saveToken`, so login is never blocked.
 */
export async function establishSession(
  token: string,
  origin: AuthOrigin,
  options?: EstablishSessionOptions,
): Promise<boolean> {
  if (!options?.force && !usesFirstPartySessionBridge()) {
    return false
  }

  return await postSession(token, origin, 1)
}

export async function upgradeSessionFromToken(
  token: string,
  origin: AuthOrigin,
  options?: UpgradeSessionOptions,
): Promise<boolean> {
  const established = await establishSession(token, origin, options)

  if (established) {
    options?.clearToken?.()
  }

  return established
}

async function postSession(token: string, origin: AuthOrigin, attempt: number): Promise<boolean> {
  let res: Response
  try {
    res = await fetch(SESSION_ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
      },
      body: JSON.stringify({ token, origin }),
    })
  } catch {
    if (attempt > 3) {
      return false
    }
    await sleep(2000)
    return postSession(token, origin, attempt + 1)
  }

  if (!res.ok) {
    if (res.status < 500) {
      return false
    }

    if (attempt > 3) {
      return false
    }
    await sleep(2000)
    return postSession(token, origin, attempt + 1)
  }

  return true
}

/**
 * Expires the server-side `fm_session` on logout. The cookie is HttpOnly so it
 * can't be cleared from JS — IAM has to revoke it. `keepalive` survives navigation.
 */
export function deleteSession(): void {
  if (!usesFirstPartySessionBridge()) {
    return
  }

  fetch(SESSION_ENDPOINT, {
    method: 'DELETE',
    credentials: 'include',
    keepalive: true,
  }).catch(() => {
    // best-effort: the cookie also expires on its own; never block logout
  })
}
